import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  Fade,
  Grow,
  CircularProgress,
  alpha,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { styled, keyframes } from "@mui/material/styles";
import {
  updateSubscriptionStatus,
  updatePaymentStatus,
} from "../services/payment.service";

// Define animations
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
`;

const slideIn = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const glowAnimation = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(0, 171, 185, 0.2);
  }
  50% {
    box-shadow: 0 0 20px rgba(0, 171, 185, 0.4);
  }
  100% {
    box-shadow: 0 0 5px rgba(0, 171, 185, 0.2);
  }
`;

interface StyledPaperProps {
  success?: boolean;
}

const StyledPaper = styled(Paper)<StyledPaperProps>(({ theme, success }) => ({
  padding: theme.spacing(6),
  textAlign: "center",
  marginTop: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
  animation: `${slideIn} 0.5s ease-out`,
  background: success
    ? `linear-gradient(145deg, ${alpha("#00ABB9", 0.05)} 0%, ${alpha(
        "#00ABB9",
        0.1
      )} 100%)`
    : `linear-gradient(145deg, ${alpha(
        theme.palette.error.light,
        0.05
      )} 0%, ${alpha(theme.palette.error.light, 0.1)} 100%)`,
  position: "relative",
  overflow: "hidden",
  border: `1px solid ${success ? "#00ABB9" : theme.palette.error.light}`,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: success ? "#00ABB9" : theme.palette.error.main,
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  position: "relative",
  display: "inline-flex",
  "& svg": {
    fontSize: 80,
    animation: `${pulseAnimation} 2s infinite ease-in-out`,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: -10,
    left: -10,
    right: -10,
    bottom: -10,
    borderRadius: "50%",
    animation: `${glowAnimation} 2s infinite ease-in-out`,
    zIndex: -1,
  },
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  fontSize: "1.1rem",
  borderRadius: theme.spacing(3),
  transition: "all 0.3s ease",
  textTransform: "none",
  boxShadow: "none",
  backgroundColor: "#00ABB9",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(0, 171, 185, 0.25)",
    backgroundColor: "#00ABB9",
  },
}));

const steps = ["Plan Selection", "Payment Method", "Confirmation"];

const SubscriptionConfirmationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const paymentStatus = searchParams.get("status");
  const trackId = searchParams.get("track_id");
  const isSuccess = paymentStatus === "success";
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Validate required parameters
    if (!paymentStatus || !trackId) {
      console.error("Missing required URL parameters");
      navigate("/subscription");
      return;
    }

    const updateStatuses = async () => {
      try {
        setIsLoading(true);

        // Update subscription status
        await updateSubscriptionStatus(
          trackId,
          isSuccess ? "ACTIVE" : "CANCELLED"
        );

        // Update payment status
        await updatePaymentStatus(trackId, isSuccess ? "COMPLETED" : "FAILED");

        // Clear stored payment data only on successful payment
        // if (isSuccess) {
        //   localStorage.removeItem('subscription_payment_track_id');
        //   localStorage.removeItem('subscription_payment_plan_details');
        // }
      } catch (err) {
        console.error("Error updating statuses:", err);
        setError(
          "Failed to update subscription status. Please contact support."
        );
      } finally {
        setIsLoading(false);
      }
    };

    updateStatuses();
  }, [paymentStatus, trackId, isSuccess, navigate]);

  const handleContinue = () => {
    if (isSuccess) {
      // Redirect to dashboard or subscription management
      navigate("/business");
    } else {
      // Redirect back to subscription plans
      navigate("/subscription");
    }
  };

  // If missing required parameters, don't render the page
  if (!paymentStatus || !trackId) {
    return null;
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Fade in timeout={800}>
        <Box>
          <Stepper
            activeStep={2}
            alternativeLabel
            sx={{
              mb: 6,
              "& .MuiStepLabel-root .Mui-completed": {
                color: "#00ABB9",
              },
              "& .MuiStepLabel-root .Mui-active": {
                color: "#00ABB9",
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <StyledPaper elevation={0} success={isSuccess}>
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 4,
                }}
              >
                <CircularProgress size={60} sx={{ mb: 2, color: "#00ABB9" }} />
                <Typography variant="h6" sx={{ color: "text.secondary" }}>
                  Updating your subscription status...
                </Typography>
              </Box>
            ) : (
              <>
                <Grow in timeout={800}>
                  <IconWrapper>
                    {isSuccess ? (
                      <CheckCircleIcon sx={{ color: "#00ABB9" }} />
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                  </IconWrapper>
                </Grow>

                <Fade in timeout={1000}>
                  <Box>
                    <Typography
                      variant="h4"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        color: isSuccess
                          ? "#00ABB9"
                          : (theme) => theme.palette.error.main,
                        mb: 3,
                      }}
                    >
                      {isSuccess ? "Payment Successful!" : "Payment Failed"}
                    </Typography>

                    {error && (
                      <Typography
                        variant="body1"
                        color="error"
                        sx={{
                          mb: 3,
                          p: 2,
                          bgcolor: "error.lighter",
                          borderRadius: 2,
                          display: "inline-block",
                        }}
                      >
                        {error}
                      </Typography>
                    )}

                    <Typography
                      variant="body1"
                      sx={{
                        mb: 3,
                        fontSize: "1.1rem",
                        color: "text.secondary",
                        maxWidth: "500px",
                        mx: "auto",
                        lineHeight: 1.6,
                      }}
                    >
                      {isSuccess
                        ? "Thank you for your subscription! Your account has been successfully upgraded with premium features."
                        : "We were unable to process your payment. Please try again or contact our support team if the problem persists."}
                    </Typography>

                    {!isSuccess && (
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 3,
                          p: 2,
                          bgcolor: (theme) =>
                            alpha(theme.palette.error.main, 0.05),
                          borderRadius: 2,
                          display: "inline-block",
                          border: "1px solid",
                          borderColor: "error.light",
                        }}
                      >
                        Transaction ID: {trackId}
                      </Typography>
                    )}

                    <Box sx={{ mt: 4 }}>
                      <AnimatedButton
                        variant="contained"
                        color={isSuccess ? "primary" : "error"}
                        size="large"
                        onClick={handleContinue}
                        sx={{
                          bgcolor: isSuccess ? "#00ABB9" : undefined,
                          "&:hover": {
                            bgcolor: isSuccess ? "#00ABB9" : undefined,
                          },
                        }}
                      >
                        {isSuccess ? "Go to Dashboard" : "Try Again"}
                      </AnimatedButton>
                    </Box>
                  </Box>
                </Fade>
              </>
            )}
          </StyledPaper>
        </Box>
      </Fade>
    </Container>
  );
};

export default SubscriptionConfirmationPage;
